import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { useJixxeoRecorder } from '../../context/RecorderContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ShieldCheck, Monitor, Video, Mic, Info, Download, Laptop } from 'lucide-react';

const MODE_LABEL = {
  screen: 'Screen + audio',
  camera: 'Camera + mic',
  audio: 'Voice only',
};

export default function PrivacyNotice() {
  const rec = useJixxeoRecorder();
  if (!rec) return null;

  // Are we running as an installed PWA (standalone display)?
  const isStandalone = typeof window !== 'undefined' && (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );

  const modes = [
    { key: 'screen', label: 'Screen + audio',
      desc: 'Records your Jixxeo AI screen — chats, images, scrolling, everything. Your browser will show a share-screen prompt.',
      icon: Monitor, ok: true,
      unavailableNote: 'Not supported here. Try Camera or Voice below.' },
    { key: 'camera', label: 'Camera + mic',
      desc: 'Records your front camera with a "Jixxeo AI" watermark. Works on phones.',
      icon: Video, ok: rec.capabilities.camera,
      unavailableNote: 'This browser has no camera access.' },
    { key: 'audio', label: 'Voice only',
      desc: 'Captures a voice note. Smallest file, universal support.',
      icon: Mic, ok: rec.capabilities.audio,
      unavailableNote: 'This browser has no mic access.' },
  ];

  return (
    <Dialog open={rec.showPicker} onOpenChange={rec.setShowPicker}>
      <DialogContent className="glass border-white/10 max-w-md" data-testid="privacy-notice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <ShieldCheck className="w-5 h-5 text-violet-400" />
            Choose recording mode
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-slate-300 space-y-3">
          <p className="text-xs text-slate-400 leading-relaxed flex gap-2">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-500" />
            You're about to record your Jixxeo AI session. Make sure you have permission to record any other people
            or private information. Recordings are stored on this device only and always carry a "Jixxeo AI" watermark.
          </p>

          <div className="space-y-2">
            {modes.map(m => {
              const disabled = !m.ok;
              return (
                <button
                  key={m.key}
                  data-testid={`mode-${m.key}`}
                  disabled={disabled}
                  onClick={() => rec.chooseModeAndStart(m.key)}
                  className={`w-full text-left rounded-xl p-3 border transition-colors flex items-center gap-3 no-tap-highlight
                    ${disabled
                      ? 'bg-white/[0.02] border-white/5 opacity-50 cursor-not-allowed'
                      : 'bg-white/[0.04] border-white/10 hover:border-violet-500/60 hover:bg-white/[0.08]'
                    }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${disabled ? 'bg-slate-800' : 'gradient-brand'}`}>
                    <m.icon className={`w-5 h-5 ${disabled ? 'text-slate-500' : 'text-white'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold">{m.label} {disabled && <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-1">not available</span>}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{disabled ? m.unavailableNote : m.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {!rec.capabilities.screen && (rec.capabilities.camera || rec.capabilities.audio) && (
            <div data-testid="screen-info-card" className="rounded-xl border border-violet-500/25 bg-gradient-to-br from-violet-500/10 to-cyan-500/5 p-3">
              <div className="flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-lg gradient-brand flex items-center justify-center flex-shrink-0">
                  <Monitor className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold">Why is screen mode locked?</div>
                  <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                    Mobile browsers don't let web pages record the screen. To capture your whole Jixxeo AI screen, do one of these:
                  </p>
                  <ul className="text-xs text-slate-300 mt-2 space-y-1.5">
                    <li className="flex items-start gap-2">
                      <Download className="w-3.5 h-3.5 mt-0.5 text-violet-400 flex-shrink-0" />
                      <span>{isStandalone
                        ? 'You already installed the app — screen mode isn\'t exposed here yet either. Use Camera or Voice below for now.'
                        : <>Install Jixxeo AI to your home screen — tap <span className="font-semibold text-violet-300">Share → Add to Home Screen</span> and reopen.</>}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Laptop className="w-3.5 h-3.5 mt-0.5 text-cyan-400 flex-shrink-0" />
                      <span>Open Jixxeo AI on your laptop or desktop — screen recording works in Chrome, Edge, Firefox and Safari.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Video className="w-3.5 h-3.5 mt-0.5 text-emerald-400 flex-shrink-0" />
                      <span>Or use <span className="font-semibold">Camera</span> / <span className="font-semibold">Voice</span> below — both work great on mobile and still carry the Jixxeo AI watermark.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {rec.capabilities.screen && (
            <div>
              <label className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Video quality</label>
              <Select value={rec.quality} onValueChange={rec.setQuality}>
                <SelectTrigger data-testid="record-quality-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard (720p)</SelectItem>
                  <SelectItem value="hd">HD (1080p)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {!rec.anySupported && (
            <div data-testid="no-support-notice" className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              Your browser doesn't support any recording mode. Try opening Jixxeo AI in Chrome or Safari, or install it as an app.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="privacy-cancel" variant="ghost" onClick={() => rec.setShowPicker(false)}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
